/**
 * QL-343 — tenant subdomains. Each tenant is served at `<slug>.<rootDomain>`,
 * while `<rootDomain>` itself is the organization picker. Shared by the web app
 * (which host am I on?) and the API (which origin do I link to / trust?), so
 * both sides agree on what a tenant host is.
 *
 * `rootDomain` is a bare host with an optional port (`quantfolio.app`,
 * `qp.test:3000`). An empty or missing value disables subdomains entirely and
 * every helper here degrades to "not a tenant host".
 */

/**
 * Subdomains of the root domain that are used for other services (API, Idura
 * `auth`, BankID, docs, mail, …), never for a tenant. Tenant slugs may not use
 * these; the admin tenant service rejects them on create and rename.
 */
export const RESERVED_TENANT_SUBDOMAINS: readonly string[] = [
  "api",
  "apps",
  "auth",
  "bankid-prod",
  "developer",
  "docs",
  "files",
  "mail",
  "qptools",
  "status",
  "support",
  "www",
];

/** A single DNS label: lowercase letters, digits and inner hyphens, max 63 chars. */
const DNS_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** Why a slug cannot be served as a tenant subdomain, or `null` when it can. */
export function getTenantSlugHostError(slug: string): string | null {
  const code = getTenantSlugHostErrorCode(slug);
  if (code === "slug_invalid") {
    return "Slug must be 1-63 lowercase letters, numbers or hyphens, and cannot start or end with a hyphen";
  }
  if (code === "slug_reserved") {
    return `Slug "${slug}" is reserved`;
  }
  return null;
}

/** {@link getTenantSlugHostError} as a stable code, for UIs that word the error themselves. */
export function getTenantSlugHostErrorCode(slug: string): "slug_invalid" | "slug_reserved" | null {
  if (!DNS_LABEL.test(slug)) return "slug_invalid";
  if (RESERVED_TENANT_SUBDOMAINS.includes(slug)) return "slug_reserved";
  return null;
}

/**
 * QL-351 — days a retired slug keeps resolving to (and redirecting to) its tenant,
 * during which no other tenant can claim it.
 */
export const TENANT_SLUG_QUARANTINE_DAYS = 30;

/** QL-358 — minimum days between two slug changes made by a tenant admin. */
export const TENANT_SLUG_CHANGE_COOLDOWN_DAYS = 30;

/**
 * Normalize a configured root domain to `host[:port]` (lowercase, no scheme,
 * path or trailing dot). Returns `null` when unset or unusable.
 */
export function normalizeRootDomain(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim().toLowerCase();
  if (trimmed === "") return null;
  try {
    const url = new URL(trimmed.includes("://") ? trimmed : `http://${trimmed}`);
    const hostname = url.hostname.replace(/\.$/, "");
    if (!hostname) return null;
    return url.port ? `${hostname}:${url.port}` : hostname;
  } catch {
    return null;
  }
}

export type TenantHostContext =
  /** Subdomains are not configured — the app behaves as a single-origin app. */
  | { kind: "disabled" }
  /** The organization picker at the root domain. */
  | { kind: "root" }
  /** A tenant's own host. */
  | { kind: "tenant"; slug: string }
  /** Any other host (reserved subdomain, preview URL, IP, …) — treated like `disabled`. */
  | { kind: "other" };

/** Classify a browser `host` (`location.host`, i.e. hostname plus optional port). */
export function resolveTenantHost(
  host: string,
  rootDomain: string | null | undefined,
): TenantHostContext {
  const root = normalizeRootDomain(rootDomain);
  if (!root) return { kind: "disabled" };

  const current = normalizeRootDomain(host);
  if (!current) return { kind: "other" };
  if (current === root) return { kind: "root" };

  const suffix = `.${root}`;
  if (!current.endsWith(suffix)) return { kind: "other" };

  const label = current.slice(0, -suffix.length);
  if (getTenantSlugHostError(label) !== null) return { kind: "other" };
  return { kind: "tenant", slug: label };
}

/** `protocol` is `"https:"`/`"http:"` (as in `location.protocol`) or a bare scheme. */
function schemeOf(protocol: string): string {
  return protocol.replace(/:?\/*$/, "");
}

/** Origin of the root domain, e.g. `https://quantfolio.app`. */
export function buildRootOrigin(protocol: string, rootDomain: string): string {
  const root = normalizeRootDomain(rootDomain);
  if (!root) throw new Error("rootDomain is not configured");
  return `${schemeOf(protocol)}://${root}`;
}

/** Origin of a tenant host, e.g. `https://acme.quantfolio.app`. */
export function buildTenantOrigin(protocol: string, slug: string, rootDomain: string): string {
  const root = normalizeRootDomain(rootDomain);
  if (!root) throw new Error("rootDomain is not configured");
  const label = slug.toLowerCase();
  const error = getTenantSlugHostError(label);
  if (error) throw new Error(error);
  return `${schemeOf(protocol)}://${label}.${root}`;
}

/**
 * Why `webUrl` (the web app's root URL) does not belong to `rootDomain`, or
 * `null` when they agree or subdomains are disabled. Tenant links are built as
 * `<webUrl scheme>://<slug>.<rootDomain>`, so a root domain that differs from
 * WEB_URL's host would silently send every tenant link to another domain.
 */
export function getWebRootDomainMismatch(
  webUrl: string,
  rootDomain: string | null | undefined,
): string | null {
  const root = normalizeRootDomain(rootDomain);
  if (!root) return null;
  let url: URL;
  try {
    url = new URL(webUrl);
  } catch {
    return `WEB_URL "${webUrl}" is not a valid URL`;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return `WEB_URL "${webUrl}" must use http or https`;
  }
  const webHost = normalizeRootDomain(url.host);
  if (webHost !== root) {
    return `WEB_ROOT_DOMAIN "${root}" must equal the host of WEB_URL ("${webHost}")`;
  }
  return null;
}

/**
 * True when `origin` (scheme + host + optional port, as sent in the `Origin`
 * header) is the root domain or a tenant host under it, served over `protocol`.
 */
export function isTenantPlatformOrigin(
  origin: string,
  protocol: string,
  rootDomain: string | null | undefined,
): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  if (url.origin !== origin.replace(/\/$/, "").toLowerCase()) return false;
  if (schemeOf(url.protocol) !== schemeOf(protocol)) return false;
  const context = resolveTenantHost(url.host, rootDomain);
  return context.kind === "root" || context.kind === "tenant";
}
